import { useEffect, useMemo } from "react";
import { useLocation, useRouter } from "@tanstack/react-router";
import { useAtom, useAtomValue } from "jotai";
import { Navigation } from "lucide-react";
import { useNextStep } from "nextstepjs";
import { Button } from "@/components/ui/button";
import { useOnboardingMissions, useStats } from "@/lib/hooks/useApi";
import { queryClient } from "@/lib/query-client";
import { apiClient } from "@/lib/api-client";
import type { ApiResponse, TournamentListResponse } from "@/lib/types/api";

import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogClose,
} from "@/components/ui/transparent-dialog";
import { cn, generateTextShadow } from "@/lib/utils";
import { homeUiReadyAtom, teleportDialogOpenAtom } from "@/lib/homeUiAtoms";
import { isErrorPageAtom } from "@/lib/atoms";
import { ShinyWavyText } from "@/components/ShinyWavyText";
import { ShinyText } from "./ShinyText";
import { usePrivy } from "@privy-io/react-auth";

const CARD_CLIP_PATH = `polygon(
    0px calc(100% - 4px),
    2px calc(100% - 4px),
    2px calc(100% - 2px),
    4px calc(100% - 2px),
    4px 100%,
    calc(100% - 4px) 100%,
    calc(100% - 4px) calc(100% - 2px),
    calc(100% - 2px) calc(100% - 2px),
    calc(100% - 2px) calc(100% - 4px),
    100% calc(100% - 4px),
    100% 4px,
    calc(100% - 2px) 4px,
    calc(100% - 2px) 2px,
    calc(100% - 4px) 2px,
    calc(100% - 4px) 0px,
    4px 0px,
    4px 2px,
    2px 2px,
    2px 4px,
    0px 4px
  )`;

// top-left and top-right corners only
const CARD_CLIP_PATH_TOP = `polygon(
  0px 100%,
  100% 100%,
  100% 8px,
  calc(100% - 4px) 8px,
  calc(100% - 4px) 4px,
  calc(100% - 8px) 4px,
  calc(100% - 8px) 0px,
  8px 0px,
  8px 4px,
  4px 4px,
  4px 8px,
  0px 8px
)`;

const BADGE_CLIP_PATH = `polygon(
  0px calc(100% - 3px),
  1px calc(100% - 3px),
  1px calc(100% - 1px),
  3px calc(100% - 1px),
  3px 100%,
  calc(100% - 3px) 100%,
  calc(100% - 3px) calc(100% - 1px),
  calc(100% - 1px) calc(100% - 1px),
  calc(100% - 1px) calc(100% - 3px),
  100% calc(100% - 3px),
  100% 3px,
  calc(100% - 1px) 3px,
  calc(100% - 1px) 1px,
  calc(100% - 3px) 1px,
  calc(100% - 3px) 0px,
  3px 0px,
  3px 1px,
  1px 1px,
  1px 3px,
  0px 3px
)`;

type TeleportMode = {
  label: string;
  description: string;
  route: "/rush" | "/koth" | "/winner-takes-all" | "/championship" | "/practice";
  modeKey?: string;
  icon: string;
  iconClassName?: string;
  bg: string;
  accentColor: string;
  badge?: string;
  prize?: string;
};

// bottom-left and bottom-right corners only
const CARD_CLIP_PATH_BOTTOM = `polygon(
  0px 0px,
  100% 0px,
  100% calc(100% - 4px),
  calc(100% - 2px) calc(100% - 4px),
  calc(100% - 2px) calc(100% - 2px),
  calc(100% - 4px) calc(100% - 2px),
  calc(100% - 4px) 100%,
  4px 100%,
  4px calc(100% - 2px),
  2px calc(100% - 2px),
  2px calc(100% - 4px),
  0px calc(100% - 4px)
)`;

const MODES: TeleportMode[] = [
  {
    label: "Rush",
    description: "Low entry. 30 seconds matches.",
    // prize: "$200",
    route: "/rush",
    modeKey: "rush",
    icon: "https://cdn.navalabs.io/icons/piggy_idle_q1vaha.png",
    bg: "https://cdn.navalabs.io/backgrounds/bg-rush_Xk9mP2.png",
    accentColor: "#FFA301",
  },
  {
    label: "King of the Hill",
    description: "1v1. Every winning streak pays more.",
    route: "/koth",
    modeKey: "koth",
    icon: "https://cdn.navalabs.io/assets/koth-crown_OFkpRG.png",
    bg: "https://cdn.navalabs.io/backgrounds/bg-koth_Lq4vD1.png",
    accentColor: "#AB5236",
  },
  // Grand Rush temporarily hidden.
  // {
  //   label: "Grand Rush",
  //   description: "Higher rewards. More trophies.",
  //   prize: "$100",
  //   route: "/grand-rush",
  //   modeKey: "grand-rush",
  //   icon: "https://cdn.navalabs.io/icons/grand-rush-piggy-idle_59NfnU.png",
  //   iconClassName: "w-8 h-8 shrink-0 object-contain scale-150",
  //   bg: "https://cdn.navalabs.io/backgrounds/bg-grand-rush_rT5nW8.png",
  //   accentColor: "#C20E5D",
  //   badge: "5x TROPHY",
  // },
  {
    label: "Winner Takes All",
    description: "One winner. Multiply your winnings.",
    route: "/winner-takes-all",
    modeKey: "wta",
    icon: "https://cdn.navalabs.io/assets/wta-banner_ebOWJz.png",
    bg: "https://cdn.navalabs.io/backgrounds/bg-wta_Ym7bH3.png",
    accentColor: "#087F6D",
  },
  {
    label: "Monthly Cup",
    description: "Top leaderboard players qualify.",
    route: "/championship",
    icon: "/assets/FOMC-LOGO.png",
    bg: "https://cdn.navalabs.io/backgrounds/bg-championship_Jc2sF6.png",
    accentColor: "#202F5B",
  },
  // Alpha Dome temporarily hidden.
  // {
  //   label: "Alpha Dome",
  //   description: "Private matches with friends and communities.",
  //   route: "/hosted-tournament",
  //   modeKey: "alpha-dome",
  //   icon: "/assets/alpha-dome.png",
  //   bg: "https://cdn.navalabs.io/backgrounds/bg-alphadome_Gz8uE0.png",
  //   accentColor: "#808E97",
  // },
  {
    label: "Practice",
    description: "Test strategies without risk.",
    route: "/practice",
    modeKey: "practice",
    icon: "https://cdn.navalabs.io/icons/whistle_AP4FKg.png",
    bg: "https://cdn.navalabs.io/backgrounds/bg-practice_Np1wA5.png",
    accentColor: "#088550",
  },
];

export function TeleportWidget() {
  const location = useLocation();
  const router = useRouter();
  const { authenticated, user } = usePrivy();
  const [isOpen, setIsOpen] = useAtom(teleportDialogOpenAtom);
  const isHomeUiReady = useAtomValue(homeUiReadyAtom);
  const isErrorPage = useAtomValue(isErrorPageAtom);
  const { currentTour, currentStep, closeNextStep } = useNextStep();

  const walletAddress = user?.wallet?.address;
  const { data: statsData } = useStats(walletAddress ?? "", false);
  const userId = statsData?.userId;
  const { data: missionsData } = useOnboardingMissions(userId);
  const lockedModes = walletAddress ? (missionsData?.locked ?? []) : [];

  // Pre-warm the WTA list cache when the modes dialog opens so clicking
  // the Winner Takes All card resolves to its tournament detail page
  // synchronously (no render-then-redirect flash on `/winner-takes-all`).
  useEffect(() => {
    if (!isOpen) return;
    queryClient.prefetchQuery({
      queryKey: ["winner-takes-all"],
      queryFn: async () => {
        const { data } = await apiClient.get<
          ApiResponse<TournamentListResponse>
        >(`/tournament/list-winner-takes-all`);
        return data.data;
      },
    });
  }, [isOpen]);

  const SEVEN_DAYS_MS = 7 * 24 * 60 * 60 * 1000;

  const orderedModes = useMemo(() => {
    if (!statsData?.isOnboardingMission) return MODES;

    const firstQuest = missionsData?.quests?.[0];
    if (!firstQuest) return MODES;

    let practiceFirst = false;

    if (!firstQuest.isCompleted) {
      practiceFirst = true;
    } else if (!firstQuest.isClaimed) {
      practiceFirst = true;
    } else {
      const completedAt = statsData?.completedOnboardingMissionsStepOne;
      if (completedAt && Date.now() - completedAt * 1000 > SEVEN_DAYS_MS) {
        practiceFirst = false;
      } else {
        practiceFirst = true;
      }
    }

    if (!practiceFirst) return MODES;

    const practice = MODES.find((m) => m.route === "/practice");
    if (!practice) return MODES;
    return [practice, ...MODES.filter((m) => m.route !== "/practice")];
  }, [
    missionsData,
    statsData?.isOnboardingMission,
    statsData?.completedOnboardingMissionsStepOne,
  ]);

  if (
    isErrorPage ||
    location.pathname === "/" ||
    location.pathname === "/arena" ||
    location.pathname === "/referrer" ||
    location.pathname === "/terms-of-service" ||
    location.pathname === "/responsible-gaming-policy" ||
    location.pathname === "/privacy-policy" ||
    (location.pathname === "/world" && !isHomeUiReady)
  ) {
    return null;
  }

  return (
    <>
      {/* Floating Button — hidden; modes dialog is opened from HomeBottomNav */}
      {/* <div className="fixed bottom-[calc(41svh+72px)] right-0 z-999">
        <Button
          onClick={() => setIsOpen(true)}
          className="h-16 w-8 z-100 bg-black rounded-none! text-white transition-all duration-300 hover:scale-110"
          size="icon"
          style={{
            clipPath: `
            polygon(
              0px calc(100% - 6px),
              3px calc(100% - 6px),
              3px calc(100% - 3px),
              6px calc(100% - 3px),
              6px 100%,
              100% 100%,
              100% 0px,
              6px 0px,
              6px 3px,
              3px 3px,
              3px 6px,
              0px 6px
            )
            `,
          }}
        >
          <Navigation className="w-5 h-5 fill-white" />
        </Button>
      </div> */}

      {/* Mode Selection Dialog */}
      <Dialog open={isOpen} onOpenChange={setIsOpen}>
        <DialogContent className="w-[360px]! max-w-[360px]! bg-transparent! flex flex-col items-center">
          <DialogHeader className="relative w-full shrink-0">
            <h2
              className="text-white font-black text-2xl tracking-wide text-center uppercase"
              style={{ textShadow: generateTextShadow(1) }}
            >
              Modes
            </h2>
          </DialogHeader>
          <div className="w-full overflow-y-auto max-h-[80svh] px-1 py-1">
            <div className="w-full space-y-3">
              {orderedModes.map((mode) => {
                const isLocked =
                  !!mode.modeKey && lockedModes.includes(mode.modeKey);
                return (
                  <button
                    key={mode.route}
                    data-step={
                      mode.route === "/practice"
                        ? "lobby-practice-mode-card"
                        : undefined
                    }
                    onClick={() => {
                      if (isLocked) return;
                      // Welcome tour: tapping Practice from step 2 ends tour and queues practice tour
                      if (
                        mode.route === "/practice" &&
                        currentTour === "lobbyWelcomeTour" &&
                        currentStep === 2
                      ) {
                        closeNextStep();
                        localStorage.setItem(
                          "cfl:lobby-welcome-tour-done",
                          "true",
                        );
                        sessionStorage.setItem(
                          "cfl:lobby-tour-practice-pending",
                          "true",
                        );
                      }
                      setIsOpen(false);
                      // WTA: jump straight to the active tournament's detail
                      // page if we have it cached (prefetched on dialog open).
                      // Falls back to /winner-takes-all empty state otherwise.
                      if (mode.route === "/winner-takes-all") {
                        const list =
                          queryClient.getQueryData<TournamentListResponse>([
                            "winner-takes-all",
                          ]);
                        const active = list?.find(
                          (t) => t.tournamentType === "wta_one_per_day",
                        );
                        if (active) {
                          router.navigate({
                            to: "/winner-takes-all",
                            //to: "/winner-takes-all/$tournamentId",
                            params: { tournamentId: active.tournamentId },
                          });
                          return;
                        }
                      }
                      router.navigate({ to: mode.route });
                    }}
                    disabled={isLocked}
                    className={cn(
                      "bg-black/25 border-none!",
                      // "dark-blue-container border-none!",
                      // "gray-container border-none!",
                      ` relative text-[#f9e0a6] w-full h-48 flex flex-col overflow-hidden transition-all duration-150 ease-out text-left p-0!`,
                      isLocked
                        ? "cursor-not-allowed brightness-50"
                        : "cursor-pointer hover:scale-[1.02] hover:border-[#f2c969] hover:brightness-110 hover:shadow-[0_0_0_2px_rgba(0,0,0,0.6),0_0_16px_rgba(242,201,105,0.35)] active:scale-[0.95] active:brightness-90 active:duration-75",
                    )}
                    style={{
                      imageRendering: "pixelated",
                      clipPath: CARD_CLIP_PATH,
                    }}
                  >
                    <div
                      className="absolute inset-1"
                      style={{
                        backgroundColor: mode.accentColor,
                        clipPath: CARD_CLIP_PATH,
                      }}
                    />
                    <div className="flex justify-center items-center gap-3 p-3 z-10 shrink-0 h-18">
                      <img
                        src={mode.icon}
                        alt=""
                        className={
                          mode.iconClassName ??
                          "w-8 h-8 shrink-0 object-contain"
                        }
                        style={{ imageRendering: "pixelated" }}
                      />
                      <div className="flex items-center justify-between min-w-0 flex-1">
                        <div className="flex flex-col -space-y-1 min-w-0">
                          <div className="flex items-center gap-1.5">
                            <span
                              className="text-white font-black text-base uppercase tracking-wider"
                              style={{ textShadow: generateTextShadow(1) }}
                            >
                              {mode.label}
                            </span>
                            {mode.badge && (
                              <div
                                className="flex items-center justify-center gap-1.5 px-1 py-0.5"
                                style={{
                                  clipPath: CARD_CLIP_PATH,
                                  background:
                                    "radial-gradient(ellipse at 50% 25%, #FFD500 0%, #C87A0A 45%, #7A3E00 80%, #3D1C00 100%)",
                                }}
                              >
                                <img
                                  src="https://cdn.navalabs.io/icons/trophy-icon_zozppj.png"
                                  alt="trophy"
                                  className="w-3 h-3"
                                  style={{
                                    filter:
                                      "drop-shadow(0px 1px 2px rgba(0,0,0,0.6))",
                                  }}
                                />
                                <span className="leading-none">
                                  <ShinyText
                                    text={mode.badge}
                                    className="text-xs font-black"
                                    dy={2}
                                  />
                                </span>
                              </div>
                            )}
                          </div>
                          <span
                            className="text-white text-xs font-black"
                            style={{ textShadow: generateTextShadow(0.7) }}
                          >
                            {mode.description}
                          </span>
                        </div>
                        {mode.prize && (
                          <div className="shrink-0 flex flex-col items-center gap-0.5">
                            <ShinyWavyText
                              text={mode.prize}
                              className="text-xl font-black"
                              dy={3}
                            />

                            <div className="-mt-2">
                              <ShinyWavyText
                                text={"DAILY"}
                                dy={3}
                                className="text-lg font-black"
                              />
                            </div>
                          </div>
                        )}
                      </div>
                    </div>

                    <div className="px-1 pb-1 flex-1 min-h-0 z-10 relative w-full">
                      <div className="absolute top-0 left-0 right-0 z-20">
                        <hr className="border-black/60 border-[1.5px]" />
                        <hr className="border-black/30 border-2" />
                      </div>

                      <img
                        src={mode.bg}
                        alt=""
                        className="w-full h-full object-cover"
                        style={{
                          imageRendering: "pixelated",
                          clipPath: CARD_CLIP_PATH_BOTTOM,
                        }}
                      />
                    </div>

                    {isLocked && (
                      <div className="absolute inset-0 z-30 flex items-center justify-center">
                        <img
                          src="https://cdn.navalabs.io/icons/padlock_pp8r8b.png"
                          alt="locked"
                          className="size-10 brightness-[2]"
                          style={{ imageRendering: "pixelated" }}
                        />
                      </div>
                    )}
                  </button>
                );
              })}
            </div>
          </div>
          <DialogClose asChild>
            <button
              type="button"
              className="border-2 border-white rounded-full p-2 w-fit mx-auto bg-zinc-200/25 hover:bg-zinc-200/40 hover:cursor-pointer transition-colors active:scale-95"
            >
              <img
                src="https://cdn.navalabs.io/assets/CROSS_HtRPka.png"
                alt="close"
                className="w-6 h-6"
                style={{ imageRendering: "pixelated" }}
              />
            </button>
          </DialogClose>
        </DialogContent>
      </Dialog>
    </>
  );
}
